import { useState } from 'react'
import { Button, Checkbox, Divider, Text } from '@fluentui/react-components'
import { Person24Regular } from '@fluentui/react-icons'
import { FluentCard } from '../../../FluentCard/FluentCard'
import { createRestClient } from '@/contexts/QueryProvider'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionStorage } from '@/hooks'
import { MessagesWorkspacesNewWorkspaceUserAudit } from '@/dotnet-client'
import { useGetSearchParam } from '@/hooks'

type ReviewUsersPanelProps = {
  workspaceId: number
  onClose: () => void
}

type DapperWorkspaceUser = {
  workspaceId?: number
  userId?: number
  accessGrantedVia?: string | null
  givenName?: string | null
  familyName?: string | null
  email?: string | null
}

type DapperWorkspaceUserGroup = {
  accessType?: string | null
  users?: Array<DapperWorkspaceUser> | null
}

export const ReviewUsersPanel = ({ workspaceId, onClose }: ReviewUsersPanelProps) => {
  const [checked, setChecked] = useState(false)
  const queryClient = useQueryClient()
  const [userId] = useSessionStorage('sub', '')
  const orgId = useGetSearchParam<number>('orgId')

  // Fetch users from API
  const { getWorkspacesApiGetWorkspaceUsers, postWorkspacesApiAddApprovedWorkspaceUsersAudit } = createRestClient()

  // Queries: Fetch workspace users
  const {
    data: workspaceUsers,
    isPending: isUsersPending,
    isError: isUsersError,
  } = getWorkspacesApiGetWorkspaceUsers.useQuery({
    path: { workspaceId },
  })

  // ✅ Use mutation with cache invalidation
  const auditMutation = postWorkspacesApiAddApprovedWorkspaceUsersAudit.useMutation(undefined, {
    onSuccess: () => {
      console.log('Audit entry added! Invalidating organisationWorkspacesWithAudit...')

      // ✅ Invalidate the parent query to trigger an update
      queryClient.invalidateQueries({ queryKey: ['organisationWorkspacesWithAudit', orgId] })

      onClose() // ✅ Close the panel after success
    },
    onError: (error: unknown) => {
      console.error('Error adding workspace user audit:', error)
    },
  })

  if (isUsersPending) return <Text>Loading users...</Text>
  if (isUsersError || !workspaceUsers) return <Text>Error loading users.</Text>

  // ✅ Transform users to ensure uniqueness per group
  const transformedGroups = workspaceUsers.map((group: DapperWorkspaceUserGroup) => {
    const uniqueUsers = new Map<number, DapperWorkspaceUser>()

    group.users?.forEach((user) => {
      if (!uniqueUsers.has(user.userId ?? 0)) {
        uniqueUsers.set(user.userId ?? 0, user)
      }
    })

    return {
      accessType: group.accessType || 'Unknown',
      users: [...uniqueUsers.values()],
    }
  })

  // ✅ Handle Confirm Button Click
  const handleConfirm = () => {
    if (!checked) return

    // Transform the data to match API expectations
    const approvedUsers = transformedGroups.flatMap((group) =>
      group.users.map((user) => ({
        user_id: user.userId, // Ensure correct field names match backend expectations
        email: user.email,
      }))
    )

    const requestData: MessagesWorkspacesNewWorkspaceUserAudit = {
      approverId: Number(userId), // Replace with logged-in user ID
      subscriptionId: workspaceId,
      approvedUsers,
    }

    auditMutation.mutate(
      { body: requestData },
      {
        onSuccess: () => {
          console.log('✅ Audit entry added! Invalidating queries...')

          //queryClient.invalidateQueries({ queryKey: ['organisationWorkspacesWithAudit', orgId] })
          queryClient.invalidateQueries({ queryKey: ['organisationWorkspaces', orgId] })

          // ✅ Instead of invalidating everything, only refetch specific queries
          queryClient.invalidateQueries({ queryKey: ['organisationWorkspacesWithAudit', orgId], refetchType: 'active' })

          onClose() // ✅ Close the panel after success
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <Checkbox
        checked={checked}
        onChange={(_, data) => setChecked(Boolean(data.checked))}
        label="I have reviewed user access to this workspace."
      />
      <Button disabled={!checked || auditMutation.isPending} appearance="primary" onClick={handleConfirm}>
        Confirm
      </Button>

      {/* ✅ Iterate Over Groups */}
      {transformedGroups.map((group) => (
        <div key={group.accessType} className="flex flex-col gap-2">
          <Text weight="semibold">{group.accessType}</Text>
          <Divider />
          {group.users.map((user) => (
            <FluentCard
              key={user.userId}
              title={`${user.givenName ?? 'No Name'} ${user.familyName ?? ''}`.trim()}
              description={user.email || 'No email available'}
              icon={<Person24Regular />}
            />
          ))}
        </div>
      ))}

      <Divider />
    </div>
  )
}
